import React, { useState, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  LinearProgress,
  Alert,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
} from '@mui/icons-material';
import { assetsApi } from '../services/assets-api';

interface AssetUploadDialogProps {
  open: boolean;
  onClose: () => void;
  currentPrefix: string;
  bucketId: string;
  onUploadComplete: () => void;
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(i > 1 ? 1 : 0)} ${units[i]}`;
}

export const AssetUploadDialog: React.FC<AssetUploadDialogProps> = ({
  open,
  onClose,
  currentPrefix,
  bucketId,
  onUploadComplete,
}) => {
  const [prefix, setPrefix] = useState(currentPrefix);
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const abortRef = useRef<(() => void) | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Reset state when dialog opens
  React.useEffect(() => {
    if (open) {
      setPrefix(currentPrefix);
      setFile(null);
      setUploading(false);
      setProgress(0);
      setError(null);
      setDragOver(false);
    }
  }, [open, currentPrefix]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      setFile(droppedFile);
      setError(null);
    }
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (selected) {
      setFile(selected);
      setError(null);
    }
  };

  const handleUpload = async () => {
    if (!file) return;

    const normalizedPrefix = prefix && !prefix.endsWith('/') ? `${prefix}/` : prefix;
    const key = `${normalizedPrefix}${file.name}`;

    setUploading(true);
    setProgress(0);
    setError(null);

    const { promise, abort } = assetsApi.uploadFile(
      bucketId,
      key,
      file,
      (loaded, total) => {
        setProgress(Math.round((loaded / total) * 100));
      },
    );

    abortRef.current = abort;

    try {
      await promise;
      onUploadComplete();
      onClose();
    } catch (err: any) {
      if (err.message !== 'Upload cancelled') {
        setError(err.message || 'Upload failed');
      }
    } finally {
      setUploading(false);
      abortRef.current = null;
    }
  };

  const handleCancel = () => {
    if (uploading && abortRef.current) {
      abortRef.current();
    } else {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={uploading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Upload Asset</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {/* Destination prefix */}
          <TextField
            label="Destination Prefix"
            value={prefix}
            onChange={(e) => setPrefix(e.target.value)}
            size="small"
            fullWidth
            disabled={uploading}
            helperText="S3 key prefix (folder path). File will be uploaded as prefix/filename."
            placeholder="e.g., assets/base-templates/vms/"
          />

          {/* Drop zone */}
          <Box
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => !uploading && fileInputRef.current?.click()}
            sx={{
              border: '2px dashed',
              borderColor: dragOver ? 'primary.main' : file ? 'success.main' : 'divider',
              borderRadius: 2,
              p: 3,
              textAlign: 'center',
              cursor: uploading ? 'default' : 'pointer',
              bgcolor: dragOver ? 'action.hover' : 'transparent',
              transition: 'all 0.2s',
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
              disabled={uploading}
            />
            {file ? (
              <Box>
                <Typography variant="body1" fontWeight="bold">
                  {file.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatFileSize(file.size)}
                </Typography>
              </Box>
            ) : (
              <Box>
                <UploadIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                <Typography variant="body1" color="text.secondary">
                  Drag & drop a file here, or click to browse
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Supports qcow2, tgz, tar, gz, iso, zip, and other formats
                </Typography>
              </Box>
            )}
          </Box>

          {/* Progress */}
          {uploading && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2">Uploading...</Typography>
                <Typography variant="body2">{progress}%</Typography>
              </Box>
              <LinearProgress variant="determinate" value={progress} />
            </Box>
          )}

          {/* Error */}
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} color={uploading ? 'error' : 'inherit'}>
          {uploading ? 'Cancel Upload' : 'Cancel'}
        </Button>
        <Button
          onClick={handleUpload}
          variant="contained"
          disabled={!file || uploading}
          startIcon={<UploadIcon />}
        >
          Upload
        </Button>
      </DialogActions>
    </Dialog>
  );
};
